<template>
  <div class="ltcs-schedule-probe-page">
    <header class="lsp-header">
      <h2 class="lsp-title">일정계획 변경 테스트</h2>
      <p class="lsp-sub">권한 100 전용 · PC 매크로 매니저로 일정계획 변경 테스트를 요청합니다</p>
    </header>

    <LtcsPaymtModalReplica
      :year-month="scheduleMonth || '2026-09'"
      :summary="job?.modalSummary || null"
      :grid="scheduleTables[0] || null"
    />

    <section class="lsp-card">
      <label class="lsp-label">기관</label>
      <v-autocomplete
        v-model="mCode"
        :items="members"
        :loading="memberLoading"
        item-title="label"
        item-value="mCode"
        placeholder="기관명·기관기호 검색"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        no-filter
        @update:search="onSearch"
      />
      <p v-if="picked && !picked.hasCert" class="lsp-warn">롱텀케어 인증서가 없습니다.</p>
    </section>

    <section class="lsp-card">
      <label class="lsp-label">일정 월</label>
      <input v-model="scheduleMonth" class="lsp-input" type="month" :disabled="!mCode || !picked?.hasCert" />
      <label class="lsp-label lsp-label-gap">요양보호사 일정</label>
      <v-autocomplete
        v-model="clspIdx"
        :items="schedules"
        :loading="scheduleLoading"
        :disabled="!mCode || !picked?.hasCert"
        item-title="label"
        item-value="clspIdx"
        placeholder="요양보호사 · 날짜 · 시간 · 수급자"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        no-data-text="이 달에 일정이 없습니다"
      />
      <p v-if="pickedSchedule" class="lsp-picked">
        수급자 {{ pickedSchedule.recipientName || '이름 없음' }}
        <span v-if="pickedSchedule.recipientCert"> · {{ pickedSchedule.recipientCert }}</span>
      </p>
      <p v-if="pickedSchedule" class="lsp-hint">{{ pickedSchedule.caregiverName }}</p>
      <p v-else class="lsp-hint">일정을 고르면 그 일정의 수급자가 같이 선택됩니다.</p>
    </section>

    <section class="lsp-card">
      <div class="lsp-pair">
        <div>
          <label class="lsp-label">변경 전 일자</label>
          <input class="lsp-input is-readonly" type="text" readonly :value="pickedSchedule?.date || ''" />
        </div>
        <div>
          <label class="lsp-label">변경 후 일자</label>
          <input v-model="afterDate" class="lsp-input" type="date" :disabled="!pickedSchedule" />
        </div>
      </div>
      <div class="lsp-pair lsp-pair-gap">
        <div>
          <label class="lsp-label">변경 전 시간</label>
          <div class="lsp-time">
            <input class="lsp-input is-readonly" type="text" readonly :value="pickedSchedule?.start || ''" />
            <span class="lsp-tilde">~</span>
            <input class="lsp-input is-readonly" type="text" readonly :value="pickedSchedule?.end || ''" />
          </div>
        </div>
        <div>
          <label class="lsp-label">변경 후 시간</label>
          <div class="lsp-time">
            <input v-model="afterStart" class="lsp-input" type="text" inputmode="numeric" maxlength="5" placeholder="15:30" :disabled="!pickedSchedule" @blur="afterStart = normalizeTime(afterStart)" />
            <span class="lsp-tilde">~</span>
            <input v-model="afterEnd" class="lsp-input" type="text" inputmode="numeric" maxlength="5" placeholder="15:30" :disabled="!pickedSchedule" @blur="afterEnd = normalizeTime(afterEnd)" />
          </div>
        </div>
      </div>
      <p v-if="changeBranch" class="lsp-branch">{{ changeBranch.title }}</p>
      <ul v-if="changeBranch" class="lsp-steps">
        <li v-for="(line, index) in changeBranch.lines" :key="index">{{ line }}</li>
      </ul>
      <p class="lsp-hint">변경 후 일자는 다음 날이고, 변경 후 시간은 변경 전보다 1시간 뒤입니다. 시간은 14:30처럼 표시됩니다.</p>
      <p v-if="restoreBranch" class="lsp-branch lsp-branch-restore">{{ restoreBranch.title }}</p>
      <p v-if="restoreBranch" class="lsp-hint">저장 및 통보를 누르지 않으면 공단 일정은 그대로입니다. 눌렀다면 이 실행에서 빠지는 것은 선택한 날의 체크뿐이고, 10월 일정은 넣지 않습니다. 아래는 그 체크를 다시 넣는 순서입니다.</p>
      <ul v-if="restoreBranch" class="lsp-steps">
        <li v-for="(line, index) in restoreBranch.lines" :key="'restore-' + index">{{ line }}</li>
      </ul>
    </section>

    <div class="lsp-actions">
      <button
        type="button"
        class="lsp-btn"
        :disabled="!canStart || !!starting || isJobActive"
        @click="start('pc')"
      >
        {{ starting === 'pc' ? '요청 중…' : '일정계획 변경 테스트 요청' }}
      </button>
      <button
        type="button"
        class="lsp-btn lsp-btn-sub"
        :disabled="!canStart || !!starting || isJobActive"
        :title="serverTestHint"
        @click="start('server')"
      >
        {{ starting === 'server' ? '시작 중…' : '서버에서 바로 테스트' }}
      </button>
      <button
        type="button"
        class="lsp-btn lsp-btn-restore"
        :disabled="!canRestore || !!starting || isJobActive"
        @click="start('restore')"
      >
        {{ starting === 'restore' ? '요청 중…' : '원래 일정 다시 체크' }}
      </button>
    </div>
    <p class="lsp-hint">
      요청하면 큐에 등록되고, PC에서 폴링 중인 매크로 매니저가 가져가 크롬으로 실행합니다.
      (서버 테스트는 이 서버 IP가 롱텀케어에 막혀 있으면 실패할 수 있습니다.)
    </p>

    <p v-if="message" class="lsp-msg" :class="{ 'is-error': isError }">{{ message }}</p>

    <section v-if="job" class="lsp-card">
      <div class="lsp-job-row">
        <p class="lsp-job">작업 {{ job.cmqIdx }} · {{ statusLabel }}</p>
        <button
          v-if="isJobActive"
          type="button"
          class="lsp-stop"
          :disabled="stopping"
          @click="stopJob"
        >
          {{ stopping ? '중단 중…' : '중단' }}
        </button>
      </div>
      <p v-if="job.progress" class="lsp-hint">{{ job.progress }}</p>
      <table v-if="job.modalSummary" class="lsp-modal-table">
        <thead>
          <tr>
            <th>성명</th>
            <th>장기요양인정번호</th>
            <th>자격</th>
            <th>인정유효기간</th>
            <th>계약기간</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ job.modalSummary.name || '-' }}</td>
            <td>{{ job.modalSummary.certNo || '-' }}</td>
            <td>{{ job.modalSummary.qualification || '-' }}</td>
            <td>{{ job.modalSummary.validTerm || '-' }}</td>
            <td>{{ job.modalSummary.contractTerm || '-' }}</td>
          </tr>
        </tbody>
      </table>
      <template v-for="(month, mi) in scheduleTables" :key="`m-${mi}`">
        <p class="lsp-table-title">급여일정등록·변경<span v-if="month.label"> · {{ month.label }}</span></p>
        <div class="lsp-modal-scroll">
          <table class="lsp-modal-table">
            <thead>
              <tr>
                <th v-for="(header, hi) in month.headers" :key="`h-${mi}-${hi}`">{{ header }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, ri) in month.rows" :key="`r-${mi}-${ri}`">
                <td v-for="(cell, ci) in row" :key="`c-${mi}-${ri}-${ci}`">{{ cell }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <p v-if="job.resultMessage" class="lsp-hint">{{ job.resultMessage }}</p>
      <ul v-if="job.logs && job.logs.length" class="lsp-logs">
        <li v-for="(line, idx) in job.logs" :key="idx">{{ line }}</li>
      </ul>
      <p v-if="job.errorMessage" class="lsp-warn">{{ job.errorMessage }}</p>
    </section>
    <p v-if="job && isJobActive && !isServerJob" class="lsp-hint">
      PC에 켜 둔 매크로 매니저가 작업을 가져가면, 그 PC에 크롬 창이 열리며 진행됩니다.
    </p>
    <p v-if="job && isServerJob" class="lsp-hint">
      서버 브라우저 화면이 안 떴으면
      <a class="lsp-link" :href="screenPageUrl" target="_blank" rel="noopener">여기를 눌러</a>
      열어 주세요.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { ltcsScheduleProbeApi } from '@/services/api'
import LtcsPaymtModalReplica from './LtcsPaymtModalReplica.vue'

type MemberRow = {
  mCode: string
  name: string
  longtermCode?: string
  hasCert: boolean
  label: string
}

type ScheduleRow = {
  clspIdx: number
  date: string
  start: string
  end: string
  caregiverName: string
  recipientName: string
  recipientCert: string
  feeName?: string | null
  label: string
}

type ModalSummary = {
  name?: string
  certNo?: string
  qualification?: string
  validTerm?: string
  contractTerm?: string
}

type ScheduleGrid = {
  headers: string[]
  rows: string[][]
}

type ScheduleMonth = ScheduleGrid & {
  label: string
}

type JobRow = {
  cmqIdx: number
  status: string
  progress?: string | null
  resultMessage?: string | null
  errorMessage?: string | null
  modalSummary?: ModalSummary | null
  scheduleGrid?: ScheduleGrid | null
  scheduleMonths?: ScheduleMonth[] | null
  debugPort?: number | null
  logs?: string[]
}

const DEFAULT_MONTH = '2026-09'
const DEFAULT_MEMBER = '혜봄'

const addOneDay = (dateKey: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey)
  if (!m) return ''
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  d.setDate(d.getDate() + 1)
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

const timeKey = (hm: string) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hm || '').trim())
  if (!m) return ''
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return ''
  return `${String(h).padStart(2, '0')}:${m[2]}`
}

/** 그 달 30일 일정 중 종료가 가장 늦은 건. 30일이 없으면 고르지 않는다. */
const pickDefaultSchedule = (rows: ScheduleRow[]) => {
  const on30 = rows.filter((row) => String(row.date || '').endsWith('-30'))
  if (!on30.length) return null
  return on30.slice().sort((a, b) => {
    const end = timeKey(b.end).localeCompare(timeKey(a.end))
    if (end) return end
    return timeKey(b.start).localeCompare(timeKey(a.start))
  })[0]
}

const addOneHour = (hm: string) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hm || '').trim())
  if (!m) return ''
  const total = (Number(m[1]) * 60 + Number(m[2]) + 60) % (24 * 60)
  const h = Math.floor(total / 60)
  const min = total % 60
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

const normalizeTime = (value: string) => {
  const m = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return value
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return value
  return `${String(h).padStart(2, '0')}:${m[2]}`
}

const seoulNow = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date())
  const pick = (type: string) => parts.find((part) => part.type === type)?.value || ''
  const hour = pick('hour') === '24' ? '00' : pick('hour')
  return { date: `${pick('year')}-${pick('month')}-${pick('day')}`, time: `${hour}:${pick('minute')}` }
}

const slotEnded = (dateKey: string, startHm: string, endHm: string, now: { date: string, time: string }) => {
  if (!dateKey) return false
  if (!endHm || !now.time) return dateKey < now.date
  const endDate = startHm && endHm < startHm ? nextDateKey(dateKey) : dateKey
  return `${now.date} ${now.time}` > `${endDate} ${endHm}`
}

const nextDateKey = (dateKey: string) => {
  const [y, m, d] = dateKey.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d + 1))
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(dt.getUTCDate()).padStart(2, '0')
  return `${dt.getUTCFullYear()}-${mm}-${dd}`
}

const yearMonthLabel = (dateKey: string) => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey)
  if (!matched) return ''
  return `${matched[1]}년 ${Number(matched[2])}월`
}

/** schedulePlanProbe.buildChangePlan 과 같은 갈래 문구 */
const describeChange = (input: {
  beforeDate: string
  afterDate: string
  origStart: string
  origEnd: string
  afterStart: string
  afterEnd: string
}) => {
  const now = seoulNow()
  const beforeDate = String(input.beforeDate || '').slice(0, 10)
  const afterDate = String(input.afterDate || '').slice(0, 10)
  const origStart = timeKey(input.origStart)
  const origEnd = timeKey(input.origEnd)
  const afterStartGiven = String(input.afterStart || '').trim()
  const afterEndGiven = String(input.afterEnd || '').trim()
  const afterStartClock = timeKey(input.afterStart)
  const afterEndClock = timeKey(input.afterEnd)
  if ((afterStartGiven && !afterStartClock) || (afterEndGiven && !afterEndClock)) {
    return {
      title: '변경 후 시간 형식 오류',
      lines: ['변경 후 시간은 14:30처럼 24시간제로 적습니다.'],
    }
  }
  const afterStart = afterStartClock || origStart
  const afterEnd = afterEndClock || origEnd
  const dateChanged = Boolean(beforeDate && afterDate && beforeDate !== afterDate)
  const timeChanged = Boolean(origStart && origEnd && (origStart !== afterStart || origEnd !== afterEnd))
  const beforeEnded = slotEnded(beforeDate, origStart, origEnd, now)
  const afterEnded = slotEnded(afterDate, afterStart, afterEnd, now)
  let kind = '변경 없음'
  if (dateChanged && timeChanged) kind = '일자와 시간 변경'
  else if (dateChanged) kind = '일자만 변경'
  else if (timeChanged) kind = '시간만 변경'
  let whenLabel = '안 끝난 날'
  if (beforeEnded && afterEnded) whenLabel = '끝난 날'
  else if (beforeEnded && !afterEnded) whenLabel = '끝난 날을 빼고 안 끝난 날에 넣기'
  else if (!beforeEnded && afterEnded) whenLabel = '안 끝난 날을 빼고 끝난 날에 넣기'
  if (kind === '변경 없음') {
    return { title: '변경 없음', lines: ['일자와 시간이 같아서 바꿀 칸이 없습니다.'] }
  }
  const beforeMonth = yearMonthLabel(beforeDate)
  const afterMonth = yearMonthLabel(afterDate)
  const dayOf = (dateKey: string) => Number(dateKey.slice(8, 10))
  if (beforeMonth !== afterMonth && timeChanged && beforeEnded) {
    const oldSlot = `${dayOf(beforeDate)}일 ${origStart}~${origEnd}`
    const newSlot = `${dayOf(afterDate)}일 ${afterStart}~${afterEnd}`
    return {
      title: '다른 달 · 끝난 날의 일자와 시간 변경',
      lines: [
        `1단 ${beforeMonth}. 끝난 ${oldSlot}만 이 달 표에서 뺀다`,
        `${beforeMonth} 조회 후 1일 요일이 그 달과 같은지 확인`,
        `${beforeMonth}: 지난일정 수정 → 수정사유 → 수정. 이 해제는 ${beforeMonth}에만 통한다`,
        `${beforeMonth} ${oldSlot} 체크 해제. 시작·종료 칸은 누르지 않는다`,
        `${beforeMonth}: 월금액 재산정`,
        '이 실행은 1단에서 멈춘다. 저장 및 통보 없이 2단 년월을 조회하면 1단 해제가 표에서 빠진다',
        `2단 ${afterMonth}. 넣을 시간은 ${afterStart}~${afterEnd}이고 옛 시간 행은 보지 않는다`,
        `${afterMonth} 조회. 1단의 지난일정 수정은 넘어오지 않는다`,
        afterEnded
          ? `${afterMonth}: 넣을 ${newSlot}도 끝났으므로 지난일정 수정 → 수정사유 → 수정 뒤에 행을 다룬다`
          : `${afterMonth}: 넣을 날은 아직 안 끝났으므로 지난일정 수정 없이 진행`,
        `${afterMonth} ${newSlot} 행이 있으면 그 날만 체크`,
        `${afterMonth} 행이 없으면 입력으로 요양요원과 ${afterStart}~${afterEnd}을 넣은 뒤 그 날만 체크`,
        `${afterMonth}: 월금액 재산정`,
        '체크와 재산정 뒤 저장 및 통보 버튼만 남기고 끝냅니다. 이 버튼은 1단만 공단에 넣습니다.',
      ],
    }
  }
  const steps: { month: string, line: string, past: boolean }[] = [
    {
      month: yearMonthLabel(beforeDate),
      past: beforeEnded,
      line: `${dayOf(beforeDate)}일 ${origStart}~${origEnd} 체크 해제`,
    },
  ]
  if (timeChanged) {
    steps.push({
      month: yearMonthLabel(afterDate),
      past: afterEnded,
      line: `${dayOf(afterDate)}일 ${afterStart}~${afterEnd} 행이 있으면 체크, 없으면 입력으로 행을 만든 뒤 체크`,
    })
  } else {
    steps.push({
      month: yearMonthLabel(afterDate),
      past: afterEnded,
      line: `${dayOf(afterDate)}일 ${origStart}~${origEnd} 체크. 그 시간 행이 없으면 입력으로 만든 뒤 체크`,
    })
  }
  const lines: string[] = []
  const months: string[] = []
  steps.forEach((step) => {
    if (!months.includes(step.month)) months.push(step.month)
  })
  months.forEach((month) => {
    const mine = steps.filter((step) => step.month === month)
    lines.push(mine.some((step) => step.past)
      ? `${month}: 지난일정 수정 → 수정사유 → 수정`
      : `${month}: 지난일정 수정 없이 날짜 칸만 변경`)
    mine.forEach((step) => lines.push(`${month} ${step.line}`))
    lines.push(`${month}: 월금액 재산정`)
  })
  if (beforeMonth !== afterMonth) {
    lines.push('이 실행은 앞 달 체크 뒤 저장 및 통보 버튼만 남기고 멈춘다. 다음 달을 조회하면 앞 달 수정이 표에서 빠진다')
  }
  lines.push(beforeMonth !== afterMonth
    ? '저장 및 통보 버튼만 남기고 끝냅니다. 이 버튼은 앞 달만 공단에 넣습니다.'
    : '체크와 재산정 뒤 저장 및 통보 버튼만 남기고 끝냅니다.')
  return { title: `${whenLabel} · ${kind}`, lines }
}

const describeRestore = (date: string, start: string, end: string) => {
  const now = seoulNow()
  const day = date.slice(0, 10)
  const startHm = timeKey(start)
  const endHm = timeKey(end)
  if (!day || !startHm || !endHm || startHm >= endHm) return null
  const ended = slotEnded(day, startHm, endHm, now)
  const month = yearMonthLabel(day)
  const slot = `${Number(day.slice(8, 10))}일 ${startHm}~${endHm}`
  const lines = [`${month} 조회 후 1일 요일이 그 달과 같은지 확인`]
  lines.push(ended
    ? `${month}: 지난일정 수정 → 수정사유 → 수정`
    : `${month}: 지난일정 수정 없이 날짜 칸만 체크`)
  lines.push(`${month} ${slot} 다시 체크. 그 시간 행이 없으면 입력으로 만든 뒤 체크`)
  lines.push(`${month}: 월금액 재산정`)
  lines.push('다른 날 체크는 해제하지 않습니다.')
  lines.push('체크와 재산정 뒤 저장 및 통보 버튼만 남기고 끝냅니다.')
  return { title: '원래 일정 다시 체크', lines }
}

const mCode = ref<string | null>(null)
const members = ref<MemberRow[]>([])
const memberLoading = ref(false)
const scheduleMonth = ref(DEFAULT_MONTH)
const schedules = ref<ScheduleRow[]>([])
const scheduleLoading = ref(false)
const clspIdx = ref<number | null>(null)
const afterDate = ref('')
const afterStart = ref('')
const afterEnd = ref('')
const starting = ref<'' | 'server' | 'pc' | 'restore'>('')
const stopping = ref(false)
const message = ref('')
const isError = ref(false)
const job = ref<JobRow | null>(null)
let searchTimer: ReturnType<typeof setTimeout> | null = null
let pollTimer: ReturnType<typeof setInterval> | null = null
let bootstrapping = true

const picked = computed(() => members.value.find((m) => m.mCode === mCode.value) || null)
const pickedSchedule = computed(() => schedules.value.find((s) => s.clspIdx === clspIdx.value) || null)
const changeBranch = computed(() => {
  const row = pickedSchedule.value
  if (!row?.date || !afterDate.value || !afterStart.value || !afterEnd.value) return null
  return describeChange({
    beforeDate: row.date,
    afterDate: afterDate.value,
    origStart: row.start,
    origEnd: row.end,
    afterStart: afterStart.value,
    afterEnd: afterEnd.value,
  })
})
const restoreBranch = computed(() => {
  const row = pickedSchedule.value
  if (!row?.date || !row.start || !row.end) return null
  return describeRestore(row.date, row.start, row.end)
})
const canStart = computed(() =>
  Boolean(
    mCode.value
    && picked.value?.hasCert
    && pickedSchedule.value?.recipientCert
    && afterDate.value
    && afterStart.value
    && afterEnd.value
  )
)
const canRestore = computed(() =>
  Boolean(mCode.value && picked.value?.hasCert && pickedSchedule.value?.date)
)

const statusLabel = computed(() => {
  const s = job.value?.status || ''
  if (s === 'pending') return '대기 중'
  if (s === 'running' || s === 'processing') return '진행 중'
  if (s === 'completed') return '완료'
  if (s === 'failed') return '실패'
  if (s === 'cancelled') return '취소'
  return s || '-'
})

const isJobActive = computed(() =>
  ['pending', 'processing', 'running'].includes(job.value?.status || '')
)

const isServerJob = computed(() => Boolean(job.value?.debugPort))

const scheduleTables = computed(() => {
  const months = job.value?.scheduleMonths
  if (Array.isArray(months) && months.length) {
    return months.filter((month) => month.headers?.length)
  }
  const grid = job.value?.scheduleGrid
  if (grid?.headers?.length) {
    return [{ label: '', headers: grid.headers, rows: grid.rows || [] }]
  }
  return []
})

const serverTestHint = '서버 IP가 롱텀케어에 일시 차단되면 실패합니다. 지금은 PC 요청을 쓰세요.'

const screenPageUrl = `${window.location.origin}/ltcs-schedule-screen`

const onSearch = (q: string) => {
  if (bootstrapping) return
  const selected = picked.value
  if (selected && q && (q.includes(selected.name) || selected.label.startsWith(q))) return
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => loadMembers(q || ''), 250)
}

const loadMembers = async (search: string) => {
  memberLoading.value = true
  try {
    const res = await ltcsScheduleProbeApi.searchMembers(search)
    const rows = (res.data?.data || []) as Array<{
      mCode: string
      name: string
      longtermCode?: string
      hasCert: boolean
    }>
    members.value = rows.map((r) => ({
      ...r,
      label: `${r.name}${r.longtermCode ? ` · ${r.longtermCode}` : ''}${r.hasCert ? '' : ' · 인증서 없음'}`,
    }))
  } catch (e: any) {
    isError.value = true
    message.value = e?.response?.data?.message || '기관 검색에 실패했습니다.'
  } finally {
    memberLoading.value = false
  }
}

const loadSchedules = async () => {
  schedules.value = []
  clspIdx.value = null
  afterDate.value = ''
  afterStart.value = ''
  afterEnd.value = ''
  if (!mCode.value || !picked.value?.hasCert || !scheduleMonth.value) return
  scheduleLoading.value = true
  try {
    const ym = scheduleMonth.value.replace('-', '')
    const res = await ltcsScheduleProbeApi.listSchedules(mCode.value, ym)
    const rows = (res.data?.data || []) as Array<Omit<ScheduleRow, 'label'>>
    schedules.value = rows.map((r) => ({
      ...r,
      label: `${r.caregiverName || '요양보호사'} · ${r.date} ${r.start}~${r.end} · ${r.recipientName || '수급자'}`,
    }))
    clspIdx.value = pickDefaultSchedule(schedules.value)?.clspIdx ?? null
  } catch (e: any) {
    isError.value = true
    message.value = e?.response?.data?.message || '일정을 불러오지 못했습니다.'
  } finally {
    scheduleLoading.value = false
  }
}

watch([mCode, scheduleMonth], () => {
  loadSchedules()
})
watch(clspIdx, () => {
  const row = pickedSchedule.value
  afterDate.value = row?.date ? addOneDay(row.date) : ''
  afterStart.value = row?.start ? addOneHour(row.start) : ''
  afterEnd.value = row?.end ? addOneHour(row.end) : ''
})

const stopPoll = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const pullJob = async () => {
  if (!job.value) return
  try {
    const res = await ltcsScheduleProbeApi.getJob(job.value.cmqIdx)
    job.value = res.data?.data || job.value
    const st = job.value?.status
    if (st === 'completed' || st === 'failed' || st === 'cancelled') stopPoll()
  } catch (e: any) {
    if (e?.response?.status === 404) {
      // 서버 재시작으로 작업 정보가 사라진 경우
      stopPoll()
      job.value = {
        ...job.value,
        status: 'failed',
        errorMessage: '서버 재시작으로 작업이 중단되었습니다. 다시 시작해 주세요.',
      }
    }
  }
}

const start = async (mode: 'server' | 'pc' | 'restore') => {
  const restore = mode === 'restore'
  if (restore) {
    if (!canRestore.value || !mCode.value || !clspIdx.value) return
  } else if (!canStart.value || !mCode.value || !clspIdx.value) return
  starting.value = mode
  isError.value = false
  message.value = ''
  stopPoll()
  try {
    const body = {
      mCode: mCode.value,
      afterDate: afterDate.value,
      afterStart: afterStart.value,
      afterEnd: afterEnd.value,
      clspIdx: clspIdx.value,
      restore,
    }
    const res = mode === 'server'
      ? await ltcsScheduleProbeApi.startServerTest(body)
      : await ltcsScheduleProbeApi.startTest(body)
    if (!res.data?.success) throw new Error(res.data?.message || '등록 실패')
    const cmqIdx = Number(res.data?.data?.cmqIdx)
    job.value = {
      cmqIdx,
      status: res.data?.data?.status || (mode === 'server' ? 'running' : 'pending'),
      progress: res.data?.data?.progress || null,
      debugPort: res.data?.data?.debugPort || null,
      logs: res.data?.data?.logs || [],
    }
    if (mode === 'server') {
      window.open(screenPageUrl, 'ltcsProbeScreen', 'width=1460,height=960')
    }
    message.value = res.data?.message || '테스트를 등록했습니다.'
    await pullJob()
    pollTimer = setInterval(pullJob, 3000)
  } catch (e: any) {
    isError.value = true
    message.value = e?.response?.data?.message || e?.message || '테스트 등록에 실패했습니다.'
  } finally {
    starting.value = ''
  }
}

const stopJob = async () => {
  if (!job.value || !isJobActive.value || stopping.value) return
  stopping.value = true
  try {
    const res = await ltcsScheduleProbeApi.stopJob(job.value.cmqIdx)
    if (res.data?.data) job.value = res.data.data
    stopPoll()
  } catch (e: any) {
    isError.value = true
    message.value = e?.response?.data?.message || '중단에 실패했습니다.'
  } finally {
    stopping.value = false
  }
}

const loadCurrentJob = async () => {
  try {
    const res = await ltcsScheduleProbeApi.getCurrent()
    const current = res.data?.data
    if (!current) return
    job.value = current
    if (['pending', 'processing', 'running'].includes(current.status)) {
      stopPoll()
      pollTimer = setInterval(pullJob, 3000)
    }
  } catch {
    /* 현재 작업 조회 실패는 무시 */
  }
}

onBeforeUnmount(stopPoll)
loadMembers(DEFAULT_MEMBER).finally(() => {
  const hit = members.value.find((m) => m.name.includes(DEFAULT_MEMBER) && m.hasCert)
    || members.value.find((m) => m.name.includes(DEFAULT_MEMBER))
  if (hit) mCode.value = hit.mCode
  bootstrapping = false
})
loadCurrentJob()
</script>
